import { getConfigs } from "../configs/Config";
import {
  setApplicantAppFeePayment,
  setApplicationTermsAndConditions,
  setNotification,
  setInitialFilterOptions,
  setApplications,
  setApplication,
  setConfiguration,
  setApplicationPaymentDetails,
  setCommsPreferences,
  resolveIssueSuccess,
} from "src/store/features/applications/applicationsSlice";
import { getHelpRequestsBulk } from "./HelpRequestService";
import { fetchAccessToken, fetchIdToken, decodeToken } from "../utils/Auth";
import { chunk } from "../utils/Utils";
import LocalStorageService from "./LocalStorageService";
import { AddendumsPost, AddendumsPostAddendums } from "src/types/Addendum";
import { mergeAddendumFormsAndResponses } from "src/utils/Addendum";
import {
  ApplicationAddendum,
  ApplicationAddendumAnswerSubmission,
} from "src/types/LeaseDomainAddendum";
import {
  LeaseEventId,
  PostLeaseResponse,
  Application,
  GetApplicationsBody,
  VoidLeaseResponse,
} from "src/types/Application";
import { GetFilterOptionsBody } from "src/types/FilterOptions";
import { AppThunk } from "src/redux/store";
import { formatArrayToString } from "src/utils/Filter";
import { PaymentType, type CommunityPaymentsResponse } from "src/types/Payment";
import {
  ApplicationConfiguration,
  CommsPreference,
  Configuration,
  Features,
} from "src/types/Redux";
import { Applicant, ScreeningVendor } from "src/types/Applicant";
import { ScreeningAlertType } from "src/types/Screening";

export const getApplications = (communityIds: string[] = []): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();

  const route = `${baseRoute}/applications/search`;

  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", fetchIdToken() || "");
    headers.append("x-access-token", fetchAccessToken() || "");
    headers.append("x-api-key", apiKey || "");
    const options = {
      method,
      headers,
      body: JSON.stringify({
        communityIds: communityIds,
      }),
    };
    fetch(route, options)
      .then((data) => data.json())
      .then((res: GetApplicationsBody) => {
        if (res && res.applications) {
          const applicationIds = res.applications.map(
            (application: Application) => {
              return application.applicationId;
            }
          );

          const applicationIdChunks = chunk(applicationIds, 100);
          applicationIdChunks.map((group) => {
            dispatch(getHelpRequestsBulk(group));
          });
        }

        dispatch(setApplications(res));
      })
      .catch((error) => {
        console.log("error on getting applications ", error);
        dispatch(setApplications({ applications: [], count: 0 }));
      });
  };
};

export const getApplicationById = (applicationId): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();
  const route = `${baseRoute}/applications/${applicationId}?includeDeletedApplicants=true`;

  return (dispatch) => {
    const method = "GET";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    const idToken = fetchIdToken();
    headers.append("Authorization", idToken || "");
    headers.append("x-api-key", apiKey || "");
    headers.append("x-access-token", fetchAccessToken() || "");

    try {
      const decodedToken = idToken
        ? decodeToken(idToken.replace(/^Bearer\s+/, ""))
        : {};
      headers.append("X-Associate-Id", decodedToken["cognito:username"] || "");
      headers.append(
        "X-Associate-Email",
        decodedToken.email || decodedToken["cognito:username"] || ""
      );
      headers.append(
        "X-Associate-Name",
        decodedToken.name || decodedToken["cognito:username"] || ""
      );
    } catch (error) {
      console.log("error decoding id token for associate headers", error);
    }

    const options = {
      method,
      headers,
    };
    fetch(route, options)
      .then((data) => data.json())
      .then((res) => {
        const { application: applicationFromLocal } =
          LocalStorageService.getApplication(applicationId);

        const application = {
          ...applicationFromLocal,
          ...res,
        };

        dispatch(setApplication(application));
        LocalStorageService.setApplicationInLocal(
          { ...application, lastFetchedDate: new Date().toJSON() },
          null
        );
      })
      .catch((error) => {
        console.log(
          `error on getting application by id - ${applicationId}`,
          error
        );
      });
  };
};

export const getFilterOptions = (): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();

  const route = `${baseRoute}/filter-options`;

  return (dispatch) => {
    const method = "GET";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", fetchIdToken() || "");
    headers.append("x-api-key", apiKey || "");

    const options = {
      method,
      headers,
    };

    fetch(route, options)
      .then((data) => data.json())
      .then((res: GetFilterOptionsBody) => {
        dispatch(setInitialFilterOptions(res));
      })
      .catch((error) => {
        console.log("error on getting initial filter options ", error);
      });
  };
};

export const updateUserFilterSelection = (selectedOptions): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();

  const route = `${baseRoute}/filter-options`;

  return () => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", fetchIdToken() || "");
    headers.append("x-api-key", apiKey || "");
    const body = {
      regions: formatArrayToString(selectedOptions.regions),
      markets: formatArrayToString(selectedOptions.markets),
      communities: formatArrayToString(selectedOptions.communities),
    };
    const options = {
      method,
      headers,
      body: JSON.stringify(body),
    };
    fetch(route, options)
      .then((data) => data.json())
      .then(() => {
        /**
         * No need to do anything here. Reaching this point
         * simply means that the new filter selections were saved.
         */
      })
      .catch((error) => {
        console.log("Error on getting initial filter options ", error);
      });
  };
};

export const getApplicantPaymentStatus = (
  applicationId: string,
  applicantId: string
): AppThunk => {
  return getCommunityApplicantPaymentStatus(applicationId, applicantId);
};

export const getCommunityApplicantPaymentStatus = (
  applicationId: string,
  applicantId: string
): AppThunk => {
  return async (dispatch) => {
    try {
      const { apiKey, baseRoute } = getConfigs();
      const accessToken = sessionStorage.getItem("access_token") || "";

      const route = `${baseRoute}/applications/${applicationId}/payments`;
      const headers = new Headers({
        "Content-type": "application/json",
        Accept: "application/json",
        "x-api-key": apiKey || "",
        "x-access-token": accessToken,
      });

      const response = await fetch(route, { method: "GET", headers });
      const data: CommunityPaymentsResponse = await response.json();

      if (applicantId) {
        // If applicantId is present, we're specifically checking Application Fee payment status
        // for that applicant. Application Fees are tied to individual applicants -- multiple
        // applicants may have their own separate Application Fee payments -- so we filter by
        // payments that reference this applicantId.
        const appFee = data?.paymentAmounts?.applicationFee;

        let paymentStatus: string | undefined;

        if (appFee === 0) {
          paymentStatus = "Not Applicable";
        } else {
          paymentStatus =
            data?.payments
              ?.sort(
                (a, b) =>
                  new Date(b.createdDate).getTime() -
                  new Date(a.createdDate).getTime()
              )
              ?.find(
                (payment) =>
                  payment.paymentType === PaymentType.ApplicationFee &&
                  payment.references.some(
                    (ref) =>
                      ref.refKey === "ApplicantIds" &&
                      ref.refValue.includes(String(applicantId))
                  )
              )?.paymentStatus || undefined;
        }

        dispatch(setApplicantAppFeePayment(paymentStatus));
      } else {
        // If applicantId is NOT present, we assume the intent is to fetch Security Deposit or
        // Increased Security Deposit payment data. These payment types *can* be tied to applicants
        // (and include ApplicantIds), but for this use case - which is showing the security deposit
        // info at the application level - we don't need to scope by applicant.
        //
        // So we store the full response and let the consuming component derive what it needs.
        dispatch(
          setApplicationPaymentDetails({
            payments: data.payments,
            paymentAmounts: data.paymentAmounts,
          })
        );
      }
    } catch (error) {
      console.error(
        "Error fetching community applicant payment status:",
        error
      );
    }
  };
};

export const getApplicationTermsAndConditions = (applicantId): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();
  const route = `${baseRoute}/terms-and-conditions/${applicantId}`;

  return (dispatch) => {
    const method = "GET";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", sessionStorage.getItem("token") || "");
    headers.append("x-api-key", apiKey || "");
    const options = {
      method,
      headers,
    };
    fetch(route, options)
      .then((data) => data.json())
      .then((res) => {
        dispatch(setApplicationTermsAndConditions(res));
      })
      .catch((error) => {
        console.log("Error on getting application documents ", error);
      });
  };
};

export const cancelApplication = (
  applicationId,
  unitId,
  cancelledBy: number | undefined = undefined,
  selectedTemplate: string | undefined = undefined
): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();
  const route = `${baseRoute}/cancel-application`;
  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", sessionStorage.getItem("token") || "");
    headers.append("x-api-key", apiKey || "");
    const body = {
      applicationId: applicationId,
      cancelledBy: cancelledBy,
      cancellationTemplate: selectedTemplate,
    };
    const options = {
      method,
      headers,
      body: JSON.stringify(body),
    };
    fetch(route, options)
      .then((response) => {
        if (!response.ok) {
          return Promise.reject(Error);
        }
        dispatch(
          setNotification({
            type: "INFO",
            message: `Application for Unit ${unitId} has been successfully cancelled`,
            origin: "cancelApplication",
          })
        );
      })
      .catch((error) => {
        console.log("Error cancelling ", error);
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to cancel Application",
            message:
              "There was an error cancelling the application. Please try again.",
            origin: "cancelApplication",
          })
        );
      });
  };
};

export const saveAddendumAnswers = (
  applicationId: number,
  addendumAnswers: AddendumsPostAddendums[]
): AppThunk => {
  const { baseRoute, apiKey } = getConfigs();
  const route = `${baseRoute}/applications/${applicationId}/addendums`;
  const accessToken = sessionStorage.getItem("access_token") || "";

  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("x-access-token", accessToken);
    headers.append("x-api-key", apiKey ?? "");
    const data: AddendumsPost = {
      id: applicationId,
      forms: addendumAnswers,
    };
    const options = {
      method,
      headers,
      body: JSON.stringify(data),
    };

    fetch(route, options)
      .then((response) => response.json())
      .then(() => {
        const { application: applicationFromLocal } =
          LocalStorageService.getApplication(applicationId);

        dispatch(
          setNotification({
            type: "INFO",
            message: "Success! Lease addendum answers submitted",
            origin: "saveAddendumAnswers",
          })
        );

        const newAddendums = mergeAddendumFormsAndResponses(
          applicationFromLocal.addendums,
          addendumAnswers
        );
        const newApplicationData = {
          ...(applicationFromLocal ?? {}),
          addendums: newAddendums,
        };

        dispatch(setApplication(newApplicationData));
        LocalStorageService.setApplicationInLocal(newApplicationData, null);
        dispatch(getApplicationById(applicationId));
      })
      .catch((error) => {
        console.log("Error saving addendum answers ", error);
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to save addendum answers",
            message: "Error trying to save Lease addendum answers",
          })
        );
      });
  };
};

export const completeAndRemoveApplication = (
  applicationId,
  unitId
): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();
  const route = `${baseRoute}/complete-and-remove`;
  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", sessionStorage.getItem("token") || "");
    headers.append("x-api-key", apiKey || "");
    const body = {
      applicationId: applicationId,
    };
    const options = {
      method,
      headers,
      body: JSON.stringify(body),
    };

    fetch(route, options)
      .then((response) => {
        if (!response.ok) {
          dispatch(
            setNotification({
              type: "ERROR",
              title: "Unable to complete Application",
              message:
                "There was an error completing the application.\nPlease try again.",
            })
          );
          return;
        }

        dispatch(
          setNotification({
            type: "INFO",
            message: `Application for Unit ${unitId} has been successfully finished`,
          })
        );
      })
      .catch((error) => {
        console.log("Error on completing application ", error);
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to complete Application",
            message:
              "There was an error completing the application.\nPlease try again.",
          })
        );
      });
  };
};

// AP2-6229-followup: HTTP_PROXY route straight to Lease Domain's
// POST /leases/{leaseId}/applicants/{applicantId}/credit (a direct API Gateway -> SQS enqueue,
// no synchronous Lambda). No FADV/TU vendor branching needed - Lease Domain only has TransUnion.
// The response is a bare SQS ack, not TU's {status, errors} shape - this just confirms the enqueue
// succeeded. AP2-6506: the actual outcome is now polled via pollLeaseDomainCreditScreeningStatus
// (SubmitCreditScreeningModal), same "tu-enqueue-ack" trigger as the legacy TU flow.
export const submitLeaseDomainCreditScreening = (
  leaseId: string,
  applicantId: string,
  applicantName: string,
  applicationId: string,
  phaseId?: string,
  configuration?: Configuration
): AppThunk<Promise<boolean>> => {
  return async (dispatch) => {
    const isPhaseLeaseDomainEnabled = getIsLeaseDomainEnabledForPhase(
      configuration,
      phaseId
    );
    if (!isPhaseLeaseDomainEnabled) {
      console.error(
        `Phase ${phaseId} is not LeaseDomain-enabled; submitLeaseDomainCreditScreening should not have been called for lease ${leaseId}`
      );
      return false;
    }

    try {
      const { baseRoute } = getConfigs();
      const route = `${baseRoute}/applicants/${applicantId}/credit?leaseId=${leaseId}`;
      // Matches PostApplicantCreditHttpProxyMethod's RequestParameters - Authorization and
      // Content-Type forwarded straight through, no x-api-key/x-access-token. LD.API's shared
      // authorizer rejects ID tokens for associate calls (resolves the principal as anonymous,
      // 403s) - must be an access token, same fix as cancelLeaseDomainApplication.
      const accessToken = fetchAccessToken();
      const headers = new Headers({
        "Content-type": "application/json",
        Accept: "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : "",
      });

      const res = await fetch(route, {
        method: "POST",
        headers,
        body: JSON.stringify({ applicationId }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error(
          "Failed to submit Lease Domain credit screening. Status:",
          res.status,
          errorText
        );
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to run credit screening",
            message:
              "There was an error submitting the credit screening. Please try again.",
          })
        );
        return false;
      }

      // AP2-6506: same "tu-enqueue-ack" origin the legacy TU flow uses - SubmitCreditScreeningModal
      // triggers polling off this marker regardless of which flow enqueued.
      dispatch(
        setNotification({
          type: "INFO",
          message: `TransUnion Credit Screening for ${applicantName} is in progress`,
          origin: "tu-enqueue-ack",
        })
      );
      return true;
    } catch (error) {
      console.error("Error submitting Lease Domain credit screening:", error);
      dispatch(
        setNotification({
          type: "ERROR",
          title: "Unable to run credit screening",
          message:
            "There was an error submitting the credit screening. Please try again.",
        })
      );
      return false;
    }
  };
};

export const submitCreditScreening = (
  applicationId,
  applicantId,
  applicantName,
  applicant?: Applicant,
  applicationConfiguration?: ApplicationConfiguration | null
): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();
  const screeningVendor = getScreeningVendor(
    applicant,
    applicationConfiguration
  );
  const route =
    screeningVendor === ScreeningVendor.TRANSUNION
      ? `${baseRoute}/run-credit-screening/${applicantId}`
      : `${baseRoute}/run-fadv-screening`;
  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", sessionStorage.getItem("token") || "");
    headers.append("x-api-key", apiKey || "");
    headers.append(
      "x-access-token",
      sessionStorage.getItem("access_token") || ""
    );
    if (screeningVendor === ScreeningVendor.TRANSUNION) {
      headers.append(
        "x-id-token",
        fetchIdToken()?.replace("Bearer ", "") || ""
      );
    }
    const body: { applicationId: string | number; applicantId?: number } = {
      applicationId:
        screeningVendor === ScreeningVendor.TRANSUNION
          ? applicationId.toString()
          : applicationId,
    };
    if (screeningVendor === ScreeningVendor.FADV) {
      body.applicantId = applicantId;
    }
    const options = {
      method,
      headers,
      body: JSON.stringify(body),
    };
    const onErrorInstructions =
      "Please click view/edit application, check each applicant's entered address and make any necessary edits, then click submit again.";
    const timeoutRetryInstructions =
      "The screening request timed out before submit completed. It may still complete asynchronously. If the status does not update, click Submit again.";
    const conciseTimeoutRetryInstructions =
      "If the status does not update, click Submit again.";

    const isRecord = (value: unknown): value is Record<string, unknown> =>
      typeof value === "object" && value !== null;

    const getString = (value: unknown): string | undefined => {
      if (typeof value !== "string") return undefined;

      const trimmedValue = value.trim();
      return trimmedValue ? trimmedValue : undefined;
    };

    const getLegacyErrorDescription = (errors: unknown): string | undefined => {
      if (!isRecord(errors)) {
        return undefined;
      }

      const badRequestErrors = errors["Bad Request"];
      if (!Array.isArray(badRequestErrors) || !badRequestErrors[0]) {
        return undefined;
      }

      const firstBadRequestError = badRequestErrors[0];
      if (typeof firstBadRequestError !== "string") {
        return undefined;
      }

      try {
        const parsed = JSON.parse(firstBadRequestError);
        return getString(parsed?.ErrorDescription);
      } catch {
        return undefined;
      }
    };

    fetch(route, options)
      .then((res) => {
        if (!res.ok) {
          // Surface the HTTP error status before attempting to parse body.
          // This prevents a 4xx/5xx body that happens to lack error/errors fields
          // from being misread as a successful enqueue (consistent with pollCreditScreeningStatus).
          return res
            .json()
            .catch(() => ({}))
            .then((body) => {
              throw Object.assign(
                new Error(body?.message || `HTTP ${res.status}`),
                { status: res.status, body }
              );
            });
        }
        return res.json();
      })
      .then((res) => {
        // AP2-6117: SharedServices POST /credit now returns HTTP 202 with { status: "accepted", message }
        // on enqueue success. The sync response no longer contains errors[] or screenings[].
        // Screening outcomes (including TU failures) are delivered asynchronously via EventBridge.
        //
        // Success condition: res.status === "accepted" for TU, or an async-timeout TU pending ACK,
        // or no error/errors field for FADV.
        const responseMessage = getString(res?.message);
        const responseErrors = res?.errors;
        const responseError = res?.error;

        const vendorErrorMessage = Array.isArray(responseErrors)
          ? getString(responseErrors[0]?.message) ||
            getString(responseErrors[0]?.detail)
          : undefined;

        const timeoutPattern =
          /(time\s?out|timed\s?out|gateway timeout|\b504\b)/i;
        const hasTimeoutSignalFromTopLevel = timeoutPattern.test(
          [responseMessage, getString(responseError)].filter(Boolean).join(" ")
        );
        const hasTimeoutSignalFromVendor =
          !!vendorErrorMessage && timeoutPattern.test(vendorErrorMessage);
        const hasTimeoutSignal =
          hasTimeoutSignalFromTopLevel || hasTimeoutSignalFromVendor;
        const hasAsyncContinuationSignal = /(asynchron|continue)/i.test(
          [responseMessage, getString(responseError)].filter(Boolean).join(" ")
        );
        const isTuAsyncTimeoutAck =
          screeningVendor === ScreeningVendor.TRANSUNION &&
          String(res?.status || "").toLowerCase() === "pending" &&
          hasTimeoutSignalFromTopLevel &&
          hasAsyncContinuationSignal;

        const isSuccess =
          screeningVendor === ScreeningVendor.TRANSUNION
            ? res?.status === "accepted" || isTuAsyncTimeoutAck
            : !res.errors && !res.error;
        if (isSuccess) {
          if (screeningVendor === ScreeningVendor.TRANSUNION) {
            // TU: 202 ACK - modal will poll via pollCreditScreeningStatus.
            // Use origin "tu-enqueue-ack" so the modal can distinguish this from
            // a terminal success notification and start polling instead of closing.
            const inProgressMessage = `${screeningVendor} Credit Screening for ${applicantName} is in progress`;
            const tuAckMessage =
              isTuAsyncTimeoutAck && responseMessage
                ? `${inProgressMessage}\n\n${responseMessage}`
                : inProgressMessage;
            dispatch(
              setNotification({
                type: "INFO",
                message: tuAckMessage,
                origin: "tu-enqueue-ack",
              })
            );
          } else {
            dispatch(
              setNotification({
                type: "INFO",
                message: `${screeningVendor} Credit Screening for ${applicantName} is in progress`,
              })
            );
          }
        } else {
          const timeoutGuidance =
            screeningVendor === ScreeningVendor.TRANSUNION && hasTimeoutSignal
              ? hasTimeoutSignalFromTopLevel
                ? conciseTimeoutRetryInstructions
                : timeoutRetryInstructions
              : undefined;
          const legacyErrorDescription =
            getLegacyErrorDescription(responseErrors);

          const message =
            screeningVendor === ScreeningVendor.TRANSUNION
              ? [
                  responseMessage,
                  timeoutGuidance,
                  onErrorInstructions,
                  vendorErrorMessage
                    ? `Details from screening service: ${vendorErrorMessage}`
                    : undefined,
                ]
                  .filter(Boolean)
                  .join("\n\n")
              : `${onErrorInstructions}${
                  legacyErrorDescription
                    ? `\n\nDetails from screening service: ${legacyErrorDescription}`
                    : ""
                }`;

          dispatch(
            setNotification({
              type: "ERROR",
              title: "Unable to run credit screening",
              message: message,
            })
          );
        }
      })
      .catch((error) => {
        console.log("Error credit screening ", error);
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to run credit screening",
            message: onErrorInstructions,
          })
        );
      });
  };
};

/**
 * AP2-6117: Polls GET /get-credit-screening/{applicantId} every 2 seconds until
 * a matching credit screening record is created for the applicant (indicating
 * the async worker has created the requested vendor record), polling exceeds
 * 180 seconds, or the poll API errors.
 *
 * Returns a cleanup function that the caller (SubmitCreditScreeningModal) should
 * invoke on unmount to prevent state updates after the component is gone.
 *
 * Stop conditions:
 *   - Matching credit record returned for the applicant -> success INFO notification, close modal
 *   - Matching credit record with Error status -> error notification, keep modal open for retry
 *   - Polling timeout (180s) -> timeout error notification
 *   - Fetch error -> error notification
 *
 * FADV flow is unaffected - this thunk is only dispatched for TU communities.
 */
export const pollCreditScreeningStatus = (
  applicantId: number,
  applicantName: string,
  screeningVendor: ScreeningVendor.FADV | ScreeningVendor.TRANSUNION
): AppThunk<() => void> => {
  return (dispatch) => {
    const { apiKey, baseRoute } = getConfigs();
    const POLL_INTERVAL_MS = 2000;
    const POLL_TIMEOUT_MS = 180_000;
    const startTime = Date.now();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    type PolledScreening = {
      screeningStatus?: string;
      type?: string;
      vendor?: string;
      screeningType?: string;
      screeningVendor?: string;
      isSoftDelete?: boolean;
      isSoftDeleted?: boolean;
    };

    const normalizeValue = (value: unknown): string =>
      typeof value === "string" ? value.trim().toLowerCase() : "";

    const isTargetCreditScreening = (screening: PolledScreening) => {
      const screeningType = normalizeValue(
        screening.type ?? screening.screeningType
      );
      const vendor = normalizeValue(
        screening.vendor ?? screening.screeningVendor
      );
      const expectedVendor = normalizeValue(screeningVendor);
      const isSoftDeleted =
        screening.isSoftDelete === true || screening.isSoftDeleted === true;

      return (
        !isSoftDeleted &&
        screeningType === "credit" &&
        vendor === expectedVendor
      );
    };

    const cleanup = () => {
      cancelled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const schedulePoll = () => {
      if (cancelled) return;

      if (Date.now() - startTime >= POLL_TIMEOUT_MS) {
        cleanup();
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to run credit screening",
            message: "Credit screening submission timed out. Please try again.",
          })
        );
        return;
      }

      const poll = async () => {
        if (cancelled) return;

        try {
          const headers = new Headers({
            "Content-type": "application/json",
            Accept: "application/json",
          });
          headers.append(
            "Authorization",
            sessionStorage.getItem("token") || ""
          );
          headers.append("x-api-key", apiKey || "");
          headers.append(
            "x-access-token",
            sessionStorage.getItem("access_token") || ""
          );

          const res = await fetch(
            `${baseRoute}/get-credit-screening/${applicantId}`,
            { method: "GET", headers }
          );

          if (cancelled) return;

          if (!res.ok) {
            cleanup();
            dispatch(
              setNotification({
                type: "ERROR",
                title: "Unable to run credit screening",
                message:
                  "Credit screening submission failed. Click Submit to retry.",
              })
            );
            return;
          }

          const data = await res.json();
          if (cancelled) return;

          // data is expected to be an array of Screening records.
          const screenings: PolledScreening[] = Array.isArray(data)
            ? data
            : data?.applicantScreenings ?? [];
          const targetCreditScreenings = screenings.filter(
            isTargetCreditScreening
          );

          if (targetCreditScreenings.length === 0) {
            // Worker has not yet created the requested credit record - schedule next poll
            if (!cancelled) {
              timeoutId = setTimeout(schedulePoll, POLL_INTERVAL_MS);
            }
            return;
          }

          cleanup();

          // Stop condition: a matching credit record (including Pending records) means
          // the async worker has run and created a DB record for this applicant. The modal's
          // job is to confirm the worker ran, not to wait for the final TU decision.
          // Final outcomes (Approved/Declined/etc.) are delivered via EventBridge/webhook.
          // Only Error status (worker-detected failure) keeps the modal open for retry.
          const hasError = targetCreditScreenings.some(
            (s) => s.screeningStatus?.toLowerCase() === "error"
          );

          if (hasError) {
            dispatch(
              setNotification({
                type: "ERROR",
                title: "Unable to run credit screening",
                message:
                  "Credit screening submission failed. Click Submit to retry.",
              })
            );
          } else {
            window.localStorage.setItem(
              "REFRESH_APPS",
              Math.random().toString()
            );
            dispatch(
              setNotification({
                type: "INFO",
                message: `${screeningVendor} Credit Screening for ${applicantName} is in progress`,
              })
            );
          }
        } catch (err) {
          if (cancelled) return;
          console.log("Error polling credit screening status", err);
          cleanup();
          dispatch(
            setNotification({
              type: "ERROR",
              title: "Unable to run credit screening",
              message:
                "Credit screening submission failed. Click Submit to retry.",
            })
          );
        }
      };

      poll();
    };

    schedulePoll();

    return cleanup;
  };
};

/**
 * AP2-6506: Lease Domain-aware version of pollCreditScreeningStatus above.
 * pollCreditScreeningStatus's GET /get-credit-screening/{applicantId} reads legacy
 * SharedServices.Screening's own database, which never sees an LD-submitted screening - there is
 * no LD-side equivalent endpoint yet, so this instead re-polls the same GET /applications/{id}
 * route the details page already uses (includeDeletedApplicants=true), and reads this applicant's
 * screenings off the response - LD's TU submission is enqueue-only (POST .../credit), so it's the
 * only way to confirm the async worker actually ran.
 *
 * Same stop conditions/timeout/cleanup contract as pollCreditScreeningStatus.
 */
export const pollLeaseDomainCreditScreeningStatus = (
  applicationId: string,
  applicantId: string,
  applicantName: string,
  preExistingScreeningIds: Set<string> = new Set(),
  onScreeningIdsSeen?: (ids: string[]) => void
): AppThunk<() => void> => {
  return (dispatch) => {
    const { apiKey, baseRoute } = getConfigs();
    const POLL_INTERVAL_MS = 2000;
    const POLL_TIMEOUT_MS = 180_000;
    const startTime = Date.now();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    type PolledScreening = {
      id?: string | number;
      screeningStatus?: string;
      screeningType?: string;
      screeningVendor?: string;
      isSoftDeleted?: boolean;
    };

    const isTargetCreditScreening = (screening: PolledScreening) =>
      screening.isSoftDeleted !== true &&
      screening.screeningType?.toLowerCase() === "credit" &&
      screening.screeningVendor?.toLowerCase() === "transunion" &&
      !preExistingScreeningIds.has(String(screening.id));

    const cleanup = () => {
      cancelled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    // A transient failure on this GET (network blip, 5xx) tells us nothing about whether this
    // attempt's own screening already exists server-side - unlike the "no match yet" case, we
    // never got a response to check. Without this, an immediate same-session retry could
    // mistake that untracked screening for the new one. Best-effort and isolated: a second
    // failure here just means we still don't know, same as before this call.
    const captureCurrentScreeningIdsBestEffort = async () => {
      if (!onScreeningIdsSeen) return;
      try {
        const headers = new Headers({
          "Content-type": "application/json",
          Accept: "application/json",
          Authorization: fetchIdToken() || "",
          "x-api-key": apiKey || "",
          "x-access-token": fetchAccessToken() || "",
        });
        const res = await fetch(
          `${baseRoute}/applications/${applicationId}?includeDeletedApplicants=true`,
          { method: "GET", headers }
        );
        if (!res.ok) return;
        const data = await res.json();
        const applicant = (data?.applicants ?? []).find(
          (a: { id?: string | number }) => String(a?.id) === String(applicantId)
        );
        const screenings: PolledScreening[] =
          applicant?.applicantScreening ?? [];
        onScreeningIdsSeen(
          screenings
            .filter(
              (s) =>
                s.id != null &&
                s.isSoftDeleted !== true &&
                s.screeningType?.toLowerCase() === "credit" &&
                s.screeningVendor?.toLowerCase() === "transunion"
            )
            .map((s) => String(s.id))
        );
      } catch {
        // Still don't know - leave the ref as it was rather than risk a second unhandled throw.
      }
    };

    const schedulePoll = () => {
      if (cancelled) return;

      if (Date.now() - startTime >= POLL_TIMEOUT_MS) {
        cleanup();
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to run credit screening",
            message: "Credit screening submission timed out. Please try again.",
          })
        );
        return;
      }

      const poll = async () => {
        if (cancelled) return;

        try {
          const headers = new Headers({
            "Content-type": "application/json",
            Accept: "application/json",
            Authorization: fetchIdToken() || "",
            "x-api-key": apiKey || "",
            "x-access-token": fetchAccessToken() || "",
          });

          const res = await fetch(
            `${baseRoute}/applications/${applicationId}?includeDeletedApplicants=true`,
            { method: "GET", headers }
          );

          if (cancelled) return;

          if (!res.ok) {
            cleanup();
            await captureCurrentScreeningIdsBestEffort();
            dispatch(
              setNotification({
                type: "ERROR",
                title: "Unable to run credit screening",
                message:
                  "Credit screening submission failed. Click Submit to retry.",
              })
            );
            return;
          }

          const data = await res.json();
          if (cancelled) return;

          const applicant = (data?.applicants ?? []).find(
            (a: { id?: string | number }) =>
              String(a?.id) === String(applicantId)
          );
          const screenings: PolledScreening[] =
            applicant?.applicantScreening ?? [];
          const targetCreditScreenings = screenings.filter(
            isTargetCreditScreening
          );

          if (targetCreditScreenings.length === 0) {
            if (!cancelled) {
              timeoutId = setTimeout(schedulePoll, POLL_INTERVAL_MS);
            }
            return;
          }

          cleanup();

          onScreeningIdsSeen?.(
            targetCreditScreenings
              .filter((s) => s.id != null)
              .map((s) => String(s.id))
          );

          const hasError = targetCreditScreenings.some(
            (s) => s.screeningStatus?.toLowerCase() === "error"
          );

          if (hasError) {
            dispatch(
              setNotification({
                type: "ERROR",
                title: "Unable to run credit screening",
                message:
                  "Credit screening submission failed. Click Submit to retry.",
              })
            );
          } else {
            window.localStorage.setItem(
              "REFRESH_APPS",
              Math.random().toString()
            );
            dispatch(
              setNotification({
                type: "INFO",
                message: `TransUnion Credit Screening for ${applicantName} is in progress`,
              })
            );
          }
        } catch (err) {
          if (cancelled) return;
          console.log(
            "Error polling Lease Domain credit screening status",
            err
          );
          cleanup();
          await captureCurrentScreeningIdsBestEffort();
          dispatch(
            setNotification({
              type: "ERROR",
              title: "Unable to run credit screening",
              message:
                "Credit screening submission failed. Click Submit to retry.",
            })
          );
        }
      };

      poll();
    };

    schedulePoll();

    return cleanup;
  };
};

export const getScreeningVendor = (
  _applicant?: Applicant | null,
  applicationConfiguration?: ApplicationConfiguration | null
): ScreeningVendor.FADV | ScreeningVendor.TRANSUNION => {
  // Vendor is fixed at application creation; credit !== true routes to FADV.
  return applicationConfiguration?.screening?.credit === true
    ? ScreeningVendor.TRANSUNION
    : ScreeningVendor.FADV;
};

// confirmDeposit and additionalDeposit (legacy "Confirm Increased Deposit Paid" flow,
// POST /application/{id}/confirm-deposit-paid and POST /applications/{id}/additional-deposit)
// were removed as part of the LeaseDomain migration cleanup - the button that triggered them
// was already permanently hidden in practice (gated on the payment/increased-security-deposit
// features being unconfigured, which is no longer the case for any community).

// Normalizes a failed applicant-update response body into the { field: string[] } shape
// ApplicantInformationModal.tsx expects.
//
// Legacy AppLease's ASP.NET ValidationProblemDetails body carries `errors` as an object of
// human-readable message arrays, e.g. { Zip: ["The Zip field is not a valid zip code."] } -
// that shape can be forwarded as-is.
//
// Lease Domain's `errors` is a different, unrelated contract: LeaseDomain.API's
// error-handler.ts sets `errors` to the thrown error's `cause`, which for applicant-edit
// validation failures (see patch-applicant.ts's throwValidation) is flat metadata like
// { errorCode: "INVALID_ZIP_FORMAT", field: "zip" } - a plain string, not a message array,
// per key. Treating that as a { field: string[] } map crashed the modal's
// `errorObj[prop].forEach(...)` on the plain-string `errorCode`/`field` values (e.g.
// "TypeError: t[e].forEach is not a function" on a 422 INVALID_ZIP_FORMAT response).
// Detect which contract we got by checking whether `errors`' values are already arrays.
const normalizeApplicantErrors = (res: {
  errors?: unknown;
  field?: string;
  message?: string;
}): Record<string, string[]> => {
  const isLegacyFieldErrorMap =
    !!res?.errors &&
    typeof res.errors === "object" &&
    !Array.isArray(res.errors) &&
    Object.values(res.errors as Record<string, unknown>).every(Array.isArray);

  if (isLegacyFieldErrorMap) {
    return res.errors as Record<string, string[]>;
  }

  const field =
    res?.field ??
    (res?.errors as { field?: string } | undefined)?.field ??
    "Error";
  return {
    [field]: [res?.message ?? "Unable to update applicant information"],
  };
};

export const updateApplicantById = (applicantId, applicantData): AppThunk => {
  const { apiKey, baseRoute } = getConfigs();

  const route = `${baseRoute}/applicants/${applicantId}`;

  return (dispatch) => {
    const method = "PUT";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("Authorization", fetchIdToken() || "");
    headers.append("x-api-key", apiKey || "");
    headers.append("x-access-token", fetchAccessToken() || "");
    const body = {
      firstName: applicantData.firstName,
      // Omitted entirely (not sent as null/undefined) when the caller leaves it out, e.g.
      // Lease Domain edits which have no free-text middleName field in their contract.
      ...("middleName" in applicantData
        ? { middleName: applicantData.middleName }
        : {}),
      lastName: applicantData.lastName,
      dob: applicantData.dob,
      address1: applicantData.address1,
      address2: applicantData.address2,
      city: applicantData.city,
      state: applicantData.state,
      zip: applicantData.zip,
      emailAddress: applicantData.emailAddress,
      nonUSPhone: applicantData.nonUSPhone,
      isSSN: applicantData.isSSN,
      smsPreference: applicantData.smsPreference,
      emailPreference: applicantData.emailPreference,
      isMiddleName: applicantData.isMiddleName,
      phoneNumberType: applicantData.phoneNumberType,
      phoneNumber: applicantData.phoneNumber,
      // used server-side to decide whether this edit routes to Lease Domain instead of legacy AppLease
      communityId: applicantData.communityId,
      leaseId: applicantData.leaseId,
    };
    const options = {
      method,
      headers,
      body: JSON.stringify(body),
    };
    return fetch(route, options)
      .then(async (response) => {
        const res = await response.json();

        // Check the actual HTTP status, not a field inside the parsed body: legacy AppLease's
        // validation-error body happens to echo a "status" field (ASP.NET's default
        // ValidationProblemDetails shape), but Lease Domain's error body has no such field, so
        // relying on res.status here silently reported every Lease Domain validation failure
        // (bad zip/state, disallowed field, etc.) as a success.
        if (!response.ok) {
          // See normalizeApplicantErrors above for why `res` can't be forwarded as-is.
          const message = JSON.stringify(normalizeApplicantErrors(res));
          dispatch(
            setNotification({
              type: "VALIDATION_ERROR",
              title: "Update Applicant Info",
              message,
            })
          );
          return;
        }

        dispatch(
          setNotification({
            type: "INFO",
            title: "Update Applicant Info",
            message: `Applicant Info for applicant ${applicantId} has been successfully updated`,
          })
        );
        return res;
      })
      .catch((error) => {
        console.log(
          `Error on updating applicant info for applicant - ${applicantId}`,
          error
        );
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Update Applicant Info",
            message: "Unable to update applicant information",
          })
        );
      });
  };
};

export const postApplicationHoldDateFromNow = (
  applicationId,
  minutes
): AppThunk => {
  const { baseRoute, apiKey } = getConfigs();

  const route = `${baseRoute}/applications/${applicationId}/holdfromnow?minutes=${minutes}`;

  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
      Accept: "application/json",
      "x-id-token": fetchIdToken()?.replace("Bearer ", "") || "",
      "x-api-key": apiKey || "",
    });
    const options = {
      method,
      headers,
    };
    fetch(route, options)
      .then((response) => {
        if (!response.ok) {
          dispatch(
            setNotification({
              type: "ERROR",
              title: `Unable to reset Hold Clock`,
              message: `There was an error resetting Hold Clock for Application ${applicationId}.\nPlease try again.`,
            })
          );
          return;
        }

        dispatch(
          setNotification({
            type: "INFO",
            message: `Hold Clock for Application ${applicationId} has been successfully reset`,
          })
        );
      })
      .catch((error) => {
        console.log("error on postApplicationHoldDateFromNow ", error);
        dispatch(
          setNotification({
            type: "ERROR",
            title: `Unable to reset Hold Clock`,
            message: `There was an error resetting Hold Clock for Application ${applicationId}.\nPlease try again.`,
          })
        );
      });
  };
};

// AP2-6227/AP2-6229: HTTP_PROXY route straight to Lease Domain's
// PATCH /admin/applications/{applicationId}/hold-clock/extend
// (LeaseDomain.API's admin-patch-application-hold-extend.ts) - no AD.API Lambda involved, same
// pattern as clearCustomerRisk/generateLeaseDomainLease. "Reset Hold Clock" and "Extend Hold
// Clock" are the same real action (confirmed) - LD.API's extend-once rule
// (HOLD_CLOCK_ALREADY_EXTENDED) matches legacy's "this is the only time you will be able to
// reset the clock" exactly. holdClockType is always 'Application' here - LD.API also supports
// a 'Parking' hold clock type, not used by this button.
export const extendLeaseDomainHoldClock = (
  applicationId: number | string,
  extensionMinutes: number,
  phaseId?: string,
  configuration?: Configuration
): AppThunk<Promise<boolean>> => {
  return async (dispatch) => {
    const isPhaseLeaseDomainEnabled = getIsLeaseDomainEnabledForPhase(
      configuration,
      phaseId
    );
    if (!isPhaseLeaseDomainEnabled) {
      console.error(
        `Phase ${phaseId} is not LeaseDomain-enabled; extendLeaseDomainHoldClock should not have been called for application ${applicationId}`
      );
      return false;
    }

    try {
      const { baseRoute } = getConfigs();
      const route = `${baseRoute}/admin/applications/${applicationId}/hold-clock/extend`;
      // This route forwards Authorization straight through (no x-api-key, no x-access-token -
      // see PatchHoldClockExtendHttpProxyMethod's RequestParameters), unlike most other proxied
      // routes in this file. LD.API's shared authorizer rejects ID tokens for associate calls
      // (resolves the principal as anonymous, 403s) - must be an access token, same fix as
      // cancelLeaseDomainApplication/submitLeaseDomainCreditScreening.
      const accessToken = fetchAccessToken();
      const headers = new Headers({
        "Content-type": "application/json",
        Accept: "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : "",
      });

      const res = await fetch(route, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          holdClockType: "Application",
          extensionMinutes,
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error(
          "Failed to extend Lease Domain hold clock. Status:",
          res.status,
          errorText
        );
        dispatch(
          setNotification({
            type: "ERROR",
            title: `Unable to reset Hold Clock`,
            message: `There was an error resetting Hold Clock for Application ${applicationId}.\nPlease try again.`,
          })
        );
        return false;
      }

      dispatch(
        setNotification({
          type: "INFO",
          message: `Hold Clock for Application ${applicationId} has been successfully reset`,
        })
      );
      return true;
    } catch (error) {
      console.error("Error extending Lease Domain hold clock:", error);
      dispatch(
        setNotification({
          type: "ERROR",
          title: `Unable to reset Hold Clock`,
          message: `There was an error resetting Hold Clock for Application ${applicationId}.\nPlease try again.`,
        })
      );
      return false;
    }
  };
};

// AP2-6229-followup: HTTP_PROXY route straight to Lease Domain's
// PUT /leases/{leaseId}/applicants/{applicantId}/cancel-application (put-applicant-cancel.ts).
// Maps isHighRiskApplication onto LD's cancel_reason_cd (AssociateCancellation/ApplicantHighRisk).
// AP2-6506: the dropdown's selection (ApplicantFraud/ResidentHistory/ResidentBehavior) now sends
// as highRiskCancellationTemplate, picking LD's dedicated high-risk cancellation email content.
export const cancelLeaseDomainApplication = (
  leaseId: string,
  applicantId: string,
  unitId: string,
  isHighRiskApplication: boolean,
  phaseId?: string,
  configuration?: Configuration,
  applicationId?: string,
  highRiskCancellationTemplate?: string
): AppThunk<Promise<boolean>> => {
  return async (dispatch) => {
    const isPhaseLeaseDomainEnabled = getIsLeaseDomainEnabledForPhase(
      configuration,
      phaseId
    );
    if (!isPhaseLeaseDomainEnabled) {
      console.error(
        `Phase ${phaseId} is not LeaseDomain-enabled; cancelLeaseDomainApplication should not have been called for lease ${leaseId}`
      );
      return false;
    }

    try {
      const { baseRoute } = getConfigs();
      const route = `${baseRoute}/applicants/${applicantId}/cancel-application?leaseId=${leaseId}`;
      // AP2-6506: LD.API's authorizer doesn't accept ID tokens - use the access token instead.
      const accessToken = fetchAccessToken();
      const headers = new Headers({
        "Content-type": "application/json",
        Accept: "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : "",
      });

      const res = await fetch(route, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          cancelReason: isHighRiskApplication
            ? "ApplicantHighRisk"
            : "AssociateCancellation",
          // AP2-6506: applicationId is logging/correlation only on LD.API's side, never trusted
          // for business logic there. highRiskCancellationTemplate picks the associate's selected
          // high-risk email content, only meaningful when isHighRiskApplication.
          applicationId,
          ...(isHighRiskApplication && highRiskCancellationTemplate
            ? { highRiskCancellationTemplate }
            : {}),
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error(
          "Failed to cancel Lease Domain application. Status:",
          res.status,
          errorText
        );
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to cancel Application",
            message:
              "There was an error cancelling the application. Please try again.",
            origin: "cancelApplication",
          })
        );
        return false;
      }

      dispatch(
        setNotification({
          type: "INFO",
          message: `Application for Unit ${unitId} has been successfully cancelled`,
          origin: "cancelApplication",
        })
      );
      return true;
    } catch (error) {
      console.error("Error cancelling Lease Domain application:", error);
      dispatch(
        setNotification({
          type: "ERROR",
          title: "Unable to cancel Application",
          message:
            "There was an error cancelling the application. Please try again.",
          origin: "cancelApplication",
        })
      );
      return false;
    }
  };
};

// AP2-6229-followup: HTTP_PROXY route straight to Lease Domain's
// PUT /leases/{leaseId}/documents/{docusignPackageId}/resend (put-resend-lease-document.ts).
// Forwards x-access-token as Authorization, not the id token - matches legacy's own postLease
// call for this same action.
export const resendLeaseDomainLease = (
  leaseId: string,
  docusignPackageId: string,
  unitId: string,
  phaseId?: string,
  configuration?: Configuration
): AppThunk<Promise<boolean>> => {
  return async (dispatch) => {
    const isPhaseLeaseDomainEnabled = getIsLeaseDomainEnabledForPhase(
      configuration,
      phaseId
    );
    if (!isPhaseLeaseDomainEnabled) {
      console.error(
        `Phase ${phaseId} is not LeaseDomain-enabled; resendLeaseDomainLease should not have been called for lease ${leaseId}`
      );
      return false;
    }

    try {
      const { baseRoute } = getConfigs();
      const route = `${baseRoute}/leases/${leaseId}/documents/${docusignPackageId}/resend`;
      const headers = new Headers({
        "Content-type": "application/json",
        Accept: "application/json",
        "x-access-token": fetchAccessToken() || "",
      });

      const res = await fetch(route, {
        method: "PUT",
        headers,
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error(
          "Failed to resend Lease Domain lease document. Status:",
          res.status,
          errorText
        );
        dispatch(
          setNotification({
            type: "ERROR",
            message: `Error sending out lease, please try again`,
            origin: "postLease",
          })
        );
        return false;
      }

      dispatch(
        setNotification({
          type: "INFO",
          message: `Lease has been successfully sent out`,
          origin: "postLease",
        })
      );
      return true;
    } catch (error) {
      console.error("Error resending Lease Domain lease document:", error);
      dispatch(
        setNotification({
          type: "ERROR",
          message: `Error sending out lease, please try again`,
          origin: "postLease",
        })
      );
      return false;
    }
  };
};

export const postLease = (
  applicationId,
  leaseEventId,
  previousLegalRent?
): AppThunk => {
  const { baseRoute, apiKey } = getConfigs();
  const route = `${baseRoute}/applications/${applicationId}/lease`;

  return (dispatch) => {
    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
      Accept: "application/json",
      "x-access-token": fetchAccessToken() || "",
      "x-api-key": apiKey || "",
    });
    let body;
    if (previousLegalRent) {
      body = JSON.stringify({
        applicationId,
        leaseEventId,
        previousLegalRent,
      });
    } else {
      body = JSON.stringify({
        applicationId,
        leaseEventId,
      });
    }

    const options = {
      method,
      headers,
      body,
    };

    return fetch(route, options)
      .then(async (response) => {
        const data: PostLeaseResponse = await response.json();
        if (!response.ok) {
          return Promise.reject(data);
        }
        return data;
      })
      .then((data: PostLeaseResponse) => {
        if (leaseEventId === LeaseEventId.LeaseGenerated) {
          dispatch(
            setNotification({
              type: "INFO",
              message: `Lease has been generated and successfully sent out`,
              origin: "postLease",
            })
          );
        } else if (leaseEventId === LeaseEventId.LeaseResent) {
          dispatch(
            setNotification({
              type: "INFO",
              message: `Lease has been successfully sent out`,
              origin: "postLease",
            })
          );
        }

        const { application: applicationFromLocal } =
          LocalStorageService.getApplication(applicationId);

        const newApplicationData = {
          ...(applicationFromLocal ?? {}),
          lease: {
            ...(applicationFromLocal?.lease || {}),
            leaseEvents: [
              data,
              ...(applicationFromLocal?.lease?.leaseEvents || []),
            ],
          },
        };

        dispatch(setApplication(newApplicationData));
        LocalStorageService.setApplicationInLocal(newApplicationData, null);
        dispatch(getApplicationById(applicationId));
      })
      .catch((error) => {
        if (leaseEventId === LeaseEventId.LeaseGenerated) {
          dispatch(
            setNotification({
              type: "ERROR",
              message: `Error generating lease, please try again`,
              origin: "postLease",
            })
          );
        } else if (leaseEventId === LeaseEventId.LeaseResent) {
          dispatch(
            setNotification({
              type: "ERROR",
              message: `Error sending out lease, please try again`,
              origin: "postLease",
            })
          );
        }
        console.log("error on postLease ", error);
      });
  };
};

// AP2-6227/AP2-6229: HTTP_PROXY route straight to Lease Domain's
// POST /leases/{leaseId}/applications/{applicationId}/generate (LeaseDomain.API#AP2-6376) -
// no AD.API Lambda involved, same pattern as clearCustomerRisk's clear/risk route. Eligibility
// uses the same getIsLeaseDomainEnabledForPhase check as clearCustomerRisk/
// resolveApplicantScreeningIssue for consistency, not id-format - this is an action endpoint,
// not the get-application-by-id read path.
//
// Gating: use the primary applicant's controls.canGenerateLease (already real for Lease Domain
// applications) instead of the legacy lease.canGenerateLease, which Lease Domain has no
// equivalent for - see the caller in LeaseProcess.tsx.
export const generateLeaseDomainLease = (
  applicationId: number | string,
  leaseId: string,
  phaseId?: string,
  configuration?: Configuration
): AppThunk<Promise<boolean>> => {
  return async (dispatch) => {
    const isPhaseLeaseDomainEnabled = getIsLeaseDomainEnabledForPhase(
      configuration,
      phaseId
    );
    if (!isPhaseLeaseDomainEnabled) {
      console.error(
        `Phase ${phaseId} is not LeaseDomain-enabled; generateLeaseDomainLease should not have been called for application ${applicationId}`
      );
      return false;
    }

    try {
      const { apiKey, baseRoute } = getConfigs();
      const route = `${baseRoute}/leases/${leaseId}/applications/${applicationId}/generate`;
      const headers = new Headers({
        "Content-type": "application/json",
        Accept: "application/json",
        "x-access-token": fetchAccessToken() || "",
        "x-api-key": apiKey || "",
      });

      const res = await fetch(route, { method: "POST", headers });

      if (!res.ok) {
        const errorText = await res.text();
        console.error(
          "Failed to generate Lease Domain lease. Status:",
          res.status,
          errorText
        );
        dispatch(
          setNotification({
            type: "ERROR",
            message: `Error generating lease, please try again`,
            origin: "postLease",
          })
        );
        return false;
      }

      dispatch(
        setNotification({
          type: "INFO",
          message: `Lease has been generated and successfully sent out`,
          origin: "postLease",
        })
      );
      await dispatch(getApplicationById(applicationId));
      return true;
    } catch (error) {
      console.error("Error generating Lease Domain lease:", error);
      dispatch(
        setNotification({
          type: "ERROR",
          message: `Error generating lease, please try again`,
          origin: "postLease",
        })
      );
      return false;
    }
  };
};

// AP2-6379: route/contract confirmed against AdminDashboard.API's post-void-documents.js handler.
// Associate identity headers follow the same pattern as getApplicationById above.
export const voidLease = (applicationId, communityId): AppThunk => {
  const { baseRoute, apiKey } = getConfigs();
  const route = `${baseRoute}/application/${applicationId}/void-documents`;

  return (dispatch) => {
    if (applicationId == null) {
      dispatch(
        setNotification({
          type: "ERROR",
          message: `Unable to void lease: missing application id`,
          origin: "voidLease",
        })
      );
      return Promise.resolve();
    }

    const method = "POST";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    const idToken = fetchIdToken();
    headers.append("Authorization", idToken || "");
    headers.append("x-api-key", apiKey || "");
    headers.append("x-access-token", fetchAccessToken() || "");

    try {
      const decodedToken = idToken
        ? decodeToken(idToken.replace(/^Bearer\s+/, ""))
        : {};
      headers.append("X-Associate-Id", decodedToken["cognito:username"] || "");
      headers.append(
        "X-Associate-Email",
        decodedToken.email || decodedToken["cognito:username"] || ""
      );
      headers.append(
        "X-Associate-Name",
        decodedToken.name || decodedToken["cognito:username"] || ""
      );
    } catch (error) {
      console.log("error decoding id token for associate headers", error);
    }

    const body = JSON.stringify({ communityId });
    const options = { method, headers, body };

    return fetch(route, options)
      .then((response) =>
        response.json().then((data: VoidLeaseResponse) => ({
          ok: response.ok,
          data,
        }))
      )
      .then(({ ok, data }) => {
        if (!ok) {
          dispatch(
            setNotification({
              type: "ERROR",
              message: data?.error || "Unable to void lease, please try again",
              origin: "voidLease",
            })
          );
          return;
        }

        dispatch(
          setNotification({
            type: "INFO",
            message: data?.message || "Lease has been successfully voided",
            origin: "voidLease",
          })
        );

        dispatch(getApplicationById(applicationId));
      })
      .catch((error) => {
        console.log("error on voidLease ", error);
        dispatch(
          setNotification({
            type: "ERROR",
            message: `Error voiding lease, please try again`,
            origin: "voidLease",
          })
        );
      });
  };
};

export const getConfigurations = (): AppThunk => {
  const { configurationsBaseRoute, configurationApiKey } = getConfigs();

  return (dispatch) => {
    const route = `${configurationsBaseRoute}`;
    const method = "GET";
    const headers = new Headers({
      "Content-type": "application/json",
    });
    headers.append("Accept", "application/json");
    headers.append("x-api-key", configurationApiKey || "");
    headers.append("x-access-token", fetchAccessToken() || "");
    const options = {
      method,
      headers,
    };
    fetch(route, options)
      .then((data) => data.json())
      .then((res) => {
        dispatch(setConfiguration(res));
      })
      .catch((error) => {
        console.log("Error retrieving configuration", error);
      });
  };
};

export const getCommsPreferencesByApplicantIds = (
  applicantIds: string[]
): AppThunk => {
  return async (dispatch) => {
    try {
      const { apiKey, baseRoute } = getConfigs();
      const accessToken = sessionStorage.getItem("access_token") || "";

      const headers = new Headers({
        "Content-type": "application/json",
        Accept: "application/json",
        "x-api-key": apiKey || "",
        "x-access-token": accessToken,
      });

      const requests = applicantIds?.map(async (id) => {
        const route = `${baseRoute}/applicants/${id}/communication`;
        const res = await fetch(route, { method: "GET", headers });
        const data = await res.json();
        return {
          applicantId: id,
          email: data.customer?.email ?? null,
          sms: data.customer?.sms ?? null,
          emailPreference: data.preferences?.email ?? false,
          smsPreference: data.preferences?.sms ?? false,
        };
      });

      const results = await Promise.allSettled(requests);
      const preferencesMap: Record<string, CommsPreference> = {};
      results.forEach((result) => {
        if (result?.status === "fulfilled") {
          const commsPreference = result?.value;
          preferencesMap[commsPreference.applicantId] = commsPreference;
        }
      });

      dispatch(setCommsPreferences(preferencesMap));
    } catch (error) {
      console.error("Error fetching comms preferences:", error);
      dispatch(setCommsPreferences({}));
    }
  };
};

// Lease Domain-routed applications (AP2-6392 follow-up): getCommsPreferencesByApplicantIds above
// proxies straight to legacy's /Applicants/{id}/Communication and has no Lease Domain equivalent
// - a Lease Domain applicantId would just 404 against it. Lease Domain instead already returns
// each applicant's smsPreference directly on the application response (fetched associate-side,
// one Comms API call per applicant, inside LeaseDomain.API's get-lease-application), so this
// reads it straight off already-fetched application data instead of making any new network call.
export const setCommsPreferencesFromLeaseDomainApplicants = (
  applicants: { id: string | number; smsPreference?: boolean | null }[]
): AppThunk => {
  return (dispatch) => {
    const preferencesMap: Record<string, CommsPreference> = {};
    applicants?.forEach((applicant) => {
      if (!applicant?.id) return;
      const applicantId = String(applicant.id);
      preferencesMap[applicantId] = {
        applicantId,
        email: null,
        sms: null,
        emailPreference: false,
        // PR review: LD returns null (not false) when the per-applicant Comms fetch failed -
        // Boolean(null) collapsed that into an explicit "opted out", misreading a Comms outage
        // as a real preference. Preserve the tri-state instead.
        smsPreference: applicant.smsPreference ?? null,
      };
    });

    dispatch(setCommsPreferences(preferencesMap));
  };
};

// AP2-6506: Lease Domain-routed applications - GET /applications/{id}/payments and GET
// /terms-and-conditions/{id} both proxy to legacy-only endpoints with no Lease Domain equivalent.
// The data they'd return is already on the applicant record from the single get-application-by-id
// call (applicationFeePaymentStatus, tcAcknowledgementDate - see lease-domain-application.formatter.js),
// so this reads it straight off already-fetched data instead of making either network call.
export const setApplicantPaymentAndTermsFromLeaseDomain = (applicant: {
  applicationFeePaymentStatus?: string | null;
  tcAcknowledgementDate?: string | null;
}): AppThunk => {
  return (dispatch) => {
    dispatch(
      setApplicantAppFeePayment(
        applicant.applicationFeePaymentStatus ?? undefined
      )
    );
    // No document/URL equivalent exists on Lease Domain - only the acknowledgement timestamp.
    // ApplicantInformationModal's displayTermsAndConditions renders without a link when url is
    // absent, matching how it already renders no section at all when this array is empty.
    dispatch(
      setApplicationTermsAndConditions(
        applicant.tcAcknowledgementDate
          ? [
              {
                id: "lease-domain-tnc",
                type: "terms_and_conditions",
                url: null,
                createdAt: applicant.tcAcknowledgementDate,
              },
            ]
          : []
      )
    );
  };
};

// Mirrors LeaseDomain.API#1194: LD's alertType is just the capitalized Issue.type
// category (Credit/Ssn/Address - see the ScreeningAlertType enum in
// src/types/Screening.ts). Legacy issue.type values are lowercase ("credit",
// "ssn", "address" - see ScreeningTypes in src/utils/Screening.ts); anything that
// doesn't map to a recognized ScreeningAlertType returns undefined so the caller
// falls back to legacy rather than guessing.
export const mapIssueTypeToAlertType = (
  issueType?: string | null
): ScreeningAlertType | undefined => {
  if (!issueType) return undefined;
  const capitalized = (issueType.charAt(0).toUpperCase() +
    issueType.slice(1).toLowerCase()) as ScreeningAlertType;
  return Object.values(ScreeningAlertType).includes(capitalized)
    ? capitalized
    : undefined;
};

export const resolveApplicantScreeningIssue = (
  applicantId: string,
  issueId: number,
  phaseId?: string,
  leaseId?: string,
  applicationId?: string,
  alertType?: ScreeningAlertType,
  configuration?: Configuration
): AppThunk<Promise<boolean>> => {
  return async (dispatch) => {
    try {
      const { apiKey, baseRoute } = getConfigs();
      const accessToken = sessionStorage.getItem("access_token") || "";

      const idToken = fetchIdToken();
      const headers = new Headers({
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-api-key": apiKey || "",
        "x-access-token": accessToken,
      });
      if (idToken) {
        headers.append("Authorization", idToken);
      }

      // Do not decode the ID token to derive clearedBy here - the API already
      // verifies this same Authorization header, so it should derive the audit
      // identity from the verified token server-side rather than trusting a
      // client-supplied value.
      const url = `${baseRoute}/applicants/${applicantId}/screening-issues/${issueId}`;

      // AP2-6228: LD-enabled phaseIds route the resolve through LeaseDomain's
      // clear/alerts endpoint; everything else keeps hitting legacy unchanged -
      // same eligibility check and fallback shape as clearCustomerRisk above.
      const isPhaseLeaseDomainEnabled = getIsLeaseDomainEnabledForPhase(
        configuration,
        phaseId
      );
      if (
        isPhaseLeaseDomainEnabled &&
        (!leaseId || !applicationId || !alertType)
      ) {
        console.error(
          `Phase ${phaseId} is LeaseDomain-enabled but applicant ${applicantId}'s issue ${issueId} is missing leaseId/applicationId/alertType; falling back to legacy resolve`
        );
      }
      const shouldUseLeaseDomain =
        isPhaseLeaseDomainEnabled &&
        !!leaseId &&
        !!applicationId &&
        !!alertType;

      const body = shouldUseLeaseDomain
        ? { leaseId, applicationId, alertType, resolved: true, phaseId }
        : { resolved: true };

      const res = await fetch(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        console.error("Failed to resolve issue. Status:", res.status);
        return false;
      }

      dispatch(resolveIssueSuccess({ applicantId, issueId }));
      return true;
    } catch (error) {
      console.error("Error resolving screening issue:", error);
      return false;
    }
  };
};

export const clearCustomerRisk = (
  highRiskApplicants: Applicant[],
  applicationId: number | string,
  phaseId?: string,
  leaseId?: string,
  configuration?: Configuration
): AppThunk<Promise<boolean>> => {
  return async (dispatch) => {
    try {
      const { apiKey, baseRoute } = getConfigs();
      const accessToken = sessionStorage.getItem("access_token") || "";

      // Get the current logged-in user from the auth token
      const token = fetchIdToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const cleanToken = token.replace(/^Bearer\s+/, "");
      const decodedToken = decodeToken(cleanToken);

      // Extract user ID (try multiple possible fields)
      const clearedBy = decodedToken["cognito:username"] || decodedToken.name;

      if (!clearedBy) {
        throw new Error("Could not extract user ID from token");
      }

      // Extract applicant IDs
      const clearedApplicantIds = highRiskApplicants
        .map((applicant) => applicant.id)
        .filter((id) => id !== undefined);

      if (clearedApplicantIds.length === 0) {
        console.error("No valid applicant IDs to clear");
        return false;
      }

      const headers = new Headers({
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-api-key": apiKey || "",
        "x-access-token": accessToken,
      });

      // AP2-6229: LD-enabled phaseIds route the clear through LeaseDomain's
      // clear/risk endpoint; everything else keeps hitting legacy unchanged.
      const isPhaseLeaseDomainEnabled = getIsLeaseDomainEnabledForPhase(
        configuration,
        phaseId
      );
      if (isPhaseLeaseDomainEnabled && !leaseId) {
        console.error(
          `Phase ${phaseId} is LeaseDomain-enabled but application ${applicationId} has no leaseId; falling back to legacy clear-risk`
        );
      }
      const shouldUseLeaseDomain = isPhaseLeaseDomainEnabled && !!leaseId;

      const url = shouldUseLeaseDomain
        ? `${baseRoute}/leases/${leaseId}/applications/${applicationId}/clear/risk`
        : `${baseRoute}/applicants/customerrisk`;

      const body = shouldUseLeaseDomain
        ? { clearedBy, clearedApplicantIds }
        : {
            clearedBy,
            clearedApplicationId: applicationId,
            clearedApplicantIds,
          };

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error(
          "Failed to clear customer risk. Status:",
          res.status,
          errorText
        );

        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to approve high-risk applicant",
            message:
              "There was an error clearing the high-risk flag. Please try again.",
            origin: "clearCustomerRisk",
          })
        );
        return false;
      }

      // Show success notification
      dispatch(
        setNotification({
          type: "INFO",
          message:
            "High-risk applicant(s) approved successfully. Risk clearance has been recorded.",
          origin: "clearCustomerRisk",
        })
      );

      // Refresh application data from backend (this will update Redux and localStorage automatically)
      await dispatch(getApplicationById(applicationId));

      return true;
    } catch (error) {
      console.error("Error clearing customer risk:", error);
      dispatch(
        setNotification({
          type: "ERROR",
          title: "Unable to approve high-risk applicant",
          message: "An unexpected error occurred. Please try again.",
          origin: "clearCustomerRisk",
        })
      );
      return false;
    }
  };
};

const isCommunityFeatureEnabled = (
  communityArray: string[] | null | undefined,
  communityId: string
) =>
  (communityArray?.length === 0 || communityArray?.includes(communityId)) ??
  false;

export const getIsIncSecDepositFeatureEnabled = (
  features: Features,
  communityId: string
): boolean =>
  isCommunityFeatureEnabled(features?.payment?.communities, communityId) &&
  isCommunityFeatureEnabled(
    features?.payment?.increasedSecurityDeposit,
    communityId
  );

export const getIsSecDepositFeatureEnabled = (
  features: Features,
  communityId: string
): boolean =>
  isCommunityFeatureEnabled(features?.payment?.communities, communityId) &&
  isCommunityFeatureEnabled(features?.payment?.securityDeposit, communityId);

export const getIsDocumentScreeningFeatureEnabled = (
  features: Features,
  communityId: string,
  applicationConfig?: ApplicationConfiguration
): boolean => {
  const appConfigDocument = applicationConfig?.screening?.document;

  if (typeof appConfigDocument === "boolean") {
    return appConfigDocument;
  }

  return (
    isCommunityFeatureEnabled(features?.screening?.communities, communityId) &&
    isCommunityFeatureEnabled(features?.screening?.document, communityId)
  );
};

export const getIsCommunityIssueResolutionFeatureEnabled = (
  features: Features,
  communityId: string
): boolean =>
  isCommunityFeatureEnabled(features?.screening?.issueResolution, communityId);

export const getIsFurtherReviewFeatureEnabled = (
  features: Features | undefined,
  communityId: string
): boolean =>
  isCommunityFeatureEnabled(
    features?.customerRisk?.furtherReviewList,
    communityId
  );

export const getIsLeaseDomainEnabledForPhase = (
  configuration: Configuration | undefined,
  phaseId: string | undefined
): boolean => {
  const nextGenPhases = configuration?.appleaseNextGenEnabledPhases;
  if (!phaseId || !nextGenPhases) return false;

  const phaseRoute = nextGenPhases.applications?.find(
    (route) => route.phaseId === phaseId
  );

  return phaseRoute
    ? phaseRoute.enabled
    : nextGenPhases.defaultRoute === "lease-domain";
};

// The addendum proxies answer JSON on success, but an API Gateway or Lambda-level failure can
// come back as text or an empty body, so the error path parses rather than assuming.
const parseErrorMessage = (body: string): string | null => {
  try {
    return JSON.parse(body)?.error ?? null;
  } catch {
    return null;
  }
};

// GET /leases/{leaseId}/addendums (get-lease-addendums.js) - proxies Lease Domain's
// GET /admin/leases/{leaseId}/addendums, returning the addendum/question catalog alongside
// each applicant's completion status.
export const fetchLeaseAddendums = (
  leaseId: string
): AppThunk<Promise<ApplicationAddendum[] | null>> => {
  return async (dispatch) => {
    try {
      const { apiKey, baseRoute } = getConfigs();
      const route = `${baseRoute}/leases/${leaseId}/addendums`;
      const headers = new Headers({
        "Content-type": "application/json",
        Accept: "application/json",
        Authorization: fetchIdToken() || "",
        "x-api-key": apiKey || "",
        "x-access-token": fetchAccessToken() || "",
      });

      const res = await fetch(route, { method: "GET", headers });

      if (!res.ok) {
        const errorText = await res.text();
        console.error(
          "Failed to fetch lease addendums. Status:",
          res.status,
          errorText
        );
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to load addendums",
            message:
              "There was an error loading the addendum catalog for this lease. Please try again.",
            origin: "fetchLeaseAddendums",
          })
        );
        return null;
      }

      const data = await res.json();

      return data.addendums ?? [];
    } catch (error) {
      console.error("Error fetching lease addendums:", error);
      dispatch(
        setNotification({
          type: "ERROR",
          title: "Unable to load addendums",
          message:
            "There was an error loading the addendum catalog for this lease. Please try again.",
          origin: "fetchLeaseAddendums",
        })
      );
      return null;
    }
  };
};

// POST /leases/{leaseId}/applicants/{applicantId}/addendums (post-submit-addendum-on-behalf.js) -
// proxies Lease Domain's POST of the same shape, submitting addendum answers on an applicant's
// behalf. Lease Domain enforces eligibility server-side (403 when answers already exist, or the
// applicant isn't eligible) - surface its message as-is rather than guessing at a friendlier one.
export const submitAddendumOnBehalf = (
  leaseId: string,
  applicantId: string,
  addendumId: string,
  answers: ApplicationAddendumAnswerSubmission[]
): AppThunk<Promise<boolean>> => {
  return async (dispatch) => {
    try {
      const { apiKey, baseRoute } = getConfigs();
      const route = `${baseRoute}/leases/${leaseId}/applicants/${applicantId}/addendums`;
      const headers = new Headers({
        "Content-type": "application/json",
        Accept: "application/json",
        Authorization: fetchIdToken() || "",
        "x-api-key": apiKey || "",
        "x-access-token": fetchAccessToken() || "",
      });

      const res = await fetch(route, {
        method: "POST",
        headers,
        body: JSON.stringify({ addendumId, answers }),
      });
      if (!res.ok) {
        const errorText = await res.text();
        console.error(
          "Failed to submit addendum on behalf. Status:",
          res.status,
          errorText
        );
        dispatch(
          setNotification({
            type: "ERROR",
            title: "Unable to submit addendum",
            message:
              parseErrorMessage(errorText) ??
              "There was an error submitting the addendum answers. Please try again.",
            origin: "submitAddendumOnBehalf",
          })
        );
        return false;
      }

      dispatch(
        setNotification({
          type: "INFO",
          message: "Addendum answers submitted successfully",
          origin: "submitAddendumOnBehalf",
        })
      );
      return true;
    } catch (error) {
      console.error("Error submitting addendum on behalf:", error);
      dispatch(
        setNotification({
          type: "ERROR",
          title: "Unable to submit addendum",
          message:
            "There was an error submitting the addendum answers. Please try again.",
          origin: "submitAddendumOnBehalf",
        })
      );
      return false;
    }
  };
};
